import React from 'react';
import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { standardRequest } from 'shared/utils/request';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import LoadingSpinner from '@grnhse/seedling/lib/birch/components/loading_spinner';
import { withLocalePrefix } from 'shared/utils/translation';
import { Document } from 'talent_matching/types';
import { useQuery } from '@tanstack/react-query';
import DocumentDisplay from 'shared/components/documents/document_display';
import ResumeLegend from './resume_legend';

const t = withLocalePrefix('talent_matching.document_viewer_modal');

type PreviewResponse =
  | { previewable: boolean; source: string | null; fileType: string }
  | string;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
`;

const BorderedFrame = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid ${FullPalette.EVERGREEN_E100};
  border-radius: 4px;
  overflow: hidden;
  background: ${FullPalette.WHITE};
  min-height: 0;
`;

const StatusContainer = styled.div`
  min-height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const IframeContainer = styled.div`
  flex: 1;
  height: 100%;
  width: 100%;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    min-height: calc(100vh - 250px);
    display: block;
  }
`;

interface DocumentContentProps {
  document: Document | null;
  hasCalibrationTerms?: boolean;
}

const DocumentContent = ({
  document,
  hasCalibrationTerms,
}: DocumentContentProps) => {
  const url = document?.previewUrl || document?.preview_path;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['documentPreview', url],
    queryFn: async () => {
      const result = await standardRequest.get<PreviewResponse>(url as string);

      let sourceUrl: string | null = null;
      let fileType: string | null = null;

      if (typeof result.data === 'string') {
        sourceUrl = result.data;
        fileType = 'image';
      } else if (result.data.source) {
        sourceUrl = result.data.source;
        fileType = result.data.fileType;
      }

      if (sourceUrl?.includes('/data/original/missing.png')) {
        throw new Error(t('anonymized_resume_missing'));
      }

      if (!sourceUrl) {
        throw new Error(t('preview_not_available'));
      }

      return { source: sourceUrl, fileType };
    },
    enabled: !!url,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  const renderDocumentContent = () => {
    if (isLoading) {
      return (
        <StatusContainer>
          <LoadingSpinner size="medium" />
        </StatusContainer>
      );
    }

    if (isError) {
      return (
        <StatusContainer>
          <Body>{(error as Error)?.message || t('preview_not_available')}</Body>
        </StatusContainer>
      );
    }

    if (data?.source && document) {
      return (
        <IframeContainer>
          <DocumentDisplay
            displayName={document.file_name}
            sourceUrl={data.source}
            fileType={data.fileType}
          />
        </IframeContainer>
      );
    }

    return null;
  };

  return (
    <Container>
      {hasCalibrationTerms && <ResumeLegend />}
      <BorderedFrame>{renderDocumentContent()}</BorderedFrame>
    </Container>
  );
};

export default DocumentContent;
